// Tagesmaterial-Download für Teilnehmer der AZAV-Maßnahmen (Online-Präsenz).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FolderOpen, FileText, Image as ImageIcon, Presentation, File } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fileIcon = (name = '') => {
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return FileText;
  if (['ppt', 'pptx'].includes(ext)) return Presentation;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return ImageIcon;
  return File;
};

export const formatSize = (b = 0) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const downloadTagesmaterialFile = async (file, getAuthHeader) => {
  try {
    const res = await axios.get(`${API}/tagesmaterial/download/${file.id}`, {
      ...getAuthHeader(),
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.original_filename || 'material';
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error('Download fehlgeschlagen');
  }
};

export const TagesmaterialCard = ({ courseId, getAuthHeader }) => {
  const [days, setDays] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tagesmaterial/kurs/${courseId}`, getAuthHeader());
      setDays(res.data.days || []);
    } catch {
      setDays([]);
    }
  }, [courseId, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  if (!days || days.length === 0) return null;

  return (
    <aside className="mb-8 rounded-xl bg-cream-100 border border-cream-300" data-testid="tagesmaterial-card">
      <div className="px-5 sm:px-6 pt-5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-terra/10 flex items-center justify-center shrink-0">
          <FolderOpen className="w-5 h-5 text-terra" strokeWidth={1.7} />
        </div>
        <div>
          <p className="font-mono text-[9.5px] uppercase font-semibold tracking-[0.22em] text-terra">Online-Präsenzkurs</p>
          <h3 className="font-display text-xl text-espresso">Ihr Tagesmaterial</h3>
        </div>
      </div>
      <div className="px-5 sm:px-6 py-4 space-y-3">
        {days.map((day) => (
          <div key={day.tag_nummer} className="rounded-lg bg-cream-50 border border-cream-300 p-4" data-testid={`tagesmaterial-tag-${day.tag_nummer}`}>
            <div className="flex items-baseline gap-2 mb-2.5">
              <span className="font-display text-lg text-terra-dark">Tag {day.tag_nummer}</span>
              {day.tag_titel && <span className="text-[13px] font-semibold text-espresso">— {day.tag_titel}</span>}
              {day.datum && <span className="text-[11px] text-sand-500 ml-auto">{day.datum}</span>}
            </div>
            <div className="space-y-1.5">
              {day.files.map((f) => {
                const Icon = fileIcon(f.original_filename);
                return (
                  <button
                    key={f.id}
                    onClick={() => downloadTagesmaterialFile(f, getAuthHeader)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md bg-cream-100 hover:bg-cream-200 border border-cream-300 text-left transition-colors"
                    data-testid={`tagesmaterial-file-${f.id}`}
                  >
                    <Icon className="w-4 h-4 text-terra shrink-0" strokeWidth={1.7} />
                    <span className="flex-1 text-[12.5px] text-espresso truncate">{f.original_filename}</span>
                    <span className="text-[10.5px] text-sand-500">{formatSize(f.size)}</span>
                    <Download className="w-3.5 h-3.5 text-sand-500" />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
};
